import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { baoMatRepository } from '../../../lib/repositories/bao-mat.repository';
import { useToast } from '../../../lib/toast';
import { boKhoiTrangKhoaTam, gopTrangKhoaTam } from '../nghiep-vu/khoa-tam';
import type { TaiKhoanKhoaTam, TrangKhoaTam } from '../types';

const khoaKhoaTam = (tenantId: string) => ['bao-mat', 'khoa-tam', tenantId] as const;

type DuLieu = InfiniteData<TrangKhoaTam, string | null>;

/** 4xx là câu trả lời của server (thiếu quyền, sai tenant): thử lại chỉ bắt chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Tài khoản đang bị khoá tạm của một tenant (ADR-0059 mục 5.3). Mỗi trang là
 * một khúc hồ sơ user-service đã tra, có thể rỗng mà vẫn còn trang sau — nút
 * "Tra tiếp" gọi `fetchNextPage`.
 */
export function useKhoaTam(tenantId: string) {
  const query = useInfiniteQuery<TrangKhoaTam, ApiError, DuLieu, ReturnType<typeof khoaKhoaTam>, string | null>({
    queryKey: khoaKhoaTam(tenantId),
    queryFn: ({ pageParam }) => baoMatRepository.khoaTam(tenantId, pageParam),
    initialPageParam: null,
    getNextPageParam: (trang) => trang.next_cursor,
    staleTime: 30_000,
    retry: thuLai,
  });
  const dong = query.data ? gopTrangKhoaTam(query.data.pages) : [];
  return { ...query, dong };
}

/**
 * Mở khoá tạm. Thành công thì bỏ người đó khỏi các trang đã tra, **không** tải
 * lại từ đầu: tải lại thì mất phần đã "Tra tiếp". Lỗi để hộp xác nhận hiện,
 * cạnh nút vừa bấm.
 */
export function useMoKhoaTam(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, TaiKhoanKhoaTam>({
    mutationFn: (nguoi) => baoMatRepository.moKhoaTam(tenantId, nguoi.user_id),
    onSuccess: (_kq, nguoi) => {
      qc.setQueryData<DuLieu>(khoaKhoaTam(tenantId), (cu) =>
        cu ? boKhoiTrangKhoaTam(cu, nguoi.user_id) : cu,
      );
      toast.success(t('baoMat.khoaTam.daMoKhoa', { ten: nguoi.full_name }));
    },
  });
}
